import Yup from "yup";

import {
    parseTransferAmount,
    resolveTransferAmountValue,
    resolveTransferSelectValue,
} from "v3/pages/transfers/logic/transferExecuteShared";

/**
 * @param {(key: string) => string} getLabel
 * @param {boolean} sendEmail
 * @returns {import("yup").ObjectSchema}
 */
export function buildTransferInternalValidationSchema(getLabel, sendEmail) {
    return Yup.object().shape({
        debitAccount: Yup.string().required(getLabel("transfers.validation.required")),
        creditAccount: Yup.string().required(getLabel("transfers.validation.required")),
        amount: Yup.string()
            .required(getLabel("transfers.validation.required"))
            .test("test", getLabel("transfers.validation.min.amount"), (a) => parseTransferAmount(a) > 0),
        email: sendEmail
            ? Yup.string()
                  .required(getLabel("transfers.validation.required"))
                  .email(getLabel("transfers.validation.invalid"))
            : Yup.string(),
    });
}

/**
 * @param {(key: string) => string} getLabel
 * @param {boolean} sendEmail
 * @returns {import("yup").ObjectSchema}
 */
export function buildTransferToContactValidationSchema(getLabel, sendEmail) {
    return Yup.object().shape({
        debitAccount: Yup.string().required(getLabel("transfers.validation.required")),
        creditAccount: Yup.string().required(getLabel("transfers.validation.required")),
        transferKind: Yup.mixed()
            .required(getLabel("transfers.validation.required"))
            .test("has-value", getLabel("transfers.validation.required"), (value) =>
                Boolean(resolveTransferSelectValue(value)),
            ),
        concept: Yup.mixed()
            .required(getLabel("transfers.validation.required"))
            .test("has-value", getLabel("transfers.validation.required"), (value) =>
                Boolean(resolveTransferSelectValue(value)),
            ),
        amount: Yup.mixed()
            .required(getLabel("transfers.validation.required"))
            .test(
                "test",
                getLabel("transfers.validation.min.amount"),
                (amount) => resolveTransferAmountValue(amount) > 0,
            ),
        email: sendEmail
            ? Yup.string()
                  .required(getLabel("transfers.validation.required"))
                  .email(getLabel("transfers.validation.invalid"))
            : Yup.string(),
    });
}

/**
 * Whether Continue must stay inactive for to-contact execute (V2 parity + SelectFieldV3 values).
 *
 * @param {Record<string, unknown>} values
 * @param {{ creditAccountConfirmed: boolean, disabledMail: boolean, sendEmail: boolean }} context
 * @returns {boolean}
 */
export function isTransferToContactContinueInactive(values, { creditAccountConfirmed, disabledMail, sendEmail }) {
    if (!creditAccountConfirmed || disabledMail) {
        return true;
    }

    if (
        !values.debitAccount ||
        !values.creditAccount ||
        !resolveTransferSelectValue(values.transferKind) ||
        !resolveTransferSelectValue(values.concept)
    ) {
        return true;
    }

    if (resolveTransferAmountValue(values.amount) <= 0) {
        return true;
    }

    if (sendEmail) {
        const email = typeof values.email === "string" ? values.email.trim() : "";
        if (!email) {
            return true;
        }
    }

    return false;
}

/**
 * Whether Continue must stay inactive for internal execute (V2 parity).
 *
 * @param {Record<string, unknown>} values
 * @param {{ sendEmail: boolean }} context
 * @returns {boolean}
 */
export function isTransferInternalContinueInactive(values, { sendEmail }) {
    if (!values.debitAccount || !values.creditAccount) {
        return true;
    }

    if (resolveTransferAmountValue(values.amount) <= 0) {
        return true;
    }

    if (sendEmail) {
        const email = typeof values.email === "string" ? values.email.trim() : "";
        if (!email) {
            return true;
        }
    }

    return false;
}

/**
 * @param {(key: string) => string} getLabel
 * @param {boolean} sendEmail
 * @returns {import("yup").ObjectSchema}
 */
export function buildTransferThirdPartiesValidationSchema(getLabel, sendEmail) {
    return Yup.object().shape({
        debitAccount: Yup.string().required(getLabel("transfers.validation.required")),
        creditAccount: Yup.string()
            .test("is-valid-credit-account", getLabel("transfers.validation.invalidFormat"), (v) => {
                if (!v) {
                    return true;
                }
                if (/^\d{22}$/.test(v)) {
                    return true;
                }
                return /^[a-zA-Z0-9.-]{6,20}$/.test(v);
            })
            .required(getLabel("transfers.validation.required")),
        transferKind: Yup.string().required(getLabel("transfers.validation.required")),
        concept: Yup.string().required(getLabel("transfers.validation.required")),
        amount: Yup.string()
            .required(getLabel("transfers.validation.required"))
            .test("test", getLabel("transfers.validation.min.amount"), (amount) => parseTransferAmount(amount) > 0),
        email: sendEmail
            ? Yup.string()
                  .required(getLabel("transfers.validation.required"))
                  .email(getLabel("transfers.validation.invalid"))
            : Yup.string(),
    });
}
